import React, { useState, ChangeEvent } from 'react';
import { galleryAPI } from '../../services/api';

interface ImageUploadProps {
  onUploadSuccess?: ((imageData: any) => void) | ((imageUrl: string, docId: string) => void);
  onUploadError?: (error: Error) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  onUploadSuccess, 
  onUploadError 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [category, setCategory] = useState<string>('');

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setIsUploading(true);
    setProgress(10); // Start progress indicator
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('image', file);
      formData.append('title', title || file.name);
      formData.append('description', description);
      formData.append('category', category);
      
      // Simulate progress (real progress tracking would require custom axios config)
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Upload image with explicit error handling
      try {
        const response = await galleryAPI.uploadImage(formData);
        console.log('Upload response:', response);
        
        // Complete progress
        clearInterval(progressInterval);
        setProgress(100);
        
        // Reset form
        setFile(null);
        setPreview(null);
        setProgress(0);
        setTitle('');
        setDescription('');
        setCategory('');
        setIsUploading(false);
        
        // Notify parent component
        if (onUploadSuccess) {
          // Support both callback types
          if (response.data && response.data.imageUrl && response.data._id) {
            // Call with separate parameters for backward compatibility
            (onUploadSuccess as (imageUrl: string, docId: string) => void)(
              response.data.imageUrl, 
              response.data._id
            );
          } else {
            // Call with the response data object directly
            (onUploadSuccess as (imageData: any) => void)(response.data);
          }
        }
      } catch (uploadError) {
        console.error('Error in uploadImage API call:', uploadError);
        throw uploadError; // Re-throw to be caught by the outer catch
      }
      
    } catch (error) {
      console.error('Error uploading image:', error);
      if (onUploadError) onUploadError(error as Error);
      setIsUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-primary">Upload New Mural</h2>
      
      <div className="mb-4">
        <label htmlFor="title" className="block text-gray-700 mb-2">Title</label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter mural title"
          disabled={isUploading}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="description" className="block text-gray-700 mb-2">Description</label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter mural description"
          rows={3}
          disabled={isUploading}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="category" className="block text-gray-700 mb-2">Category</label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={isUploading}
        >
          <option value="">Select category</option>
          <option value="indoor">Indoor</option>
          <option value="outdoor">Outdoor</option>
          <option value="commercial">Commercial</option>
          <option value="residential">Residential</option>
          <option value="abstract">Abstract</option>
          <option value="nature">Nature</option>
          <option value="urban">Urban</option>
        </select>
      </div>

      <div className="mb-4">
        <label htmlFor="image" className="block text-gray-700 mb-2">
          Image
        </label>
        <input
          id="image"
          type="file"
          onChange={handleFileChange}
          className="block w-full text-gray-700 py-2"
          accept="image/*"
          disabled={isUploading}
        />
      </div>
      
      {preview && (
        <div className="mb-4">
          <p className="text-gray-700 mb-2">Preview:</p>
          <img 
            src={preview} 
            alt="Preview" 
            className="max-h-60 rounded-md" 
          />
        </div>
      )}
      
      {isUploading && (
        <div className="mb-4">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-primary h-2.5 rounded-full" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-center mt-2">{progress}% Uploaded</p>
        </div>
      )}
      
      <button
        onClick={handleUpload}
        disabled={!file || isUploading}
        className={`w-full py-2 px-4 rounded-md text-white font-medium ${
          !file || isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
        }`}
      >
        {isUploading ? 'Uploading...' : 'Upload Mural'}
      </button>
    </div>
  );
};

export default ImageUpload;